import { useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEMO_ACCOUNT_NAME, render, timeAgo, type AccountInfo } from '@sl/core';
import { Icon } from '../../ui/Icon';
import { Menu, type MenuEntry } from '../../ui/Menu/Menu';
import { Modal } from '../../ui/Modal/Modal';
import { Button } from '../../ui/Button/Button';
import { loadLiveIndex, type LiveIndexEntry } from '../api/liveDataset';
import { LIVE_LOOK_COPY, PullCommand } from '../api/liveLook';
import { useClock } from '../clock/ClockProvider';
import { useAppConfig } from '../configContext';
import { goToDataset } from '../dataset';
import { testId } from '../testId';
import { SurfaceModeChip } from './ModeChip';
import { SHELL_COPY } from './copy';
import { useShell } from './shellContext';
import styles from './AppShell.module.css';

// THE ACCOUNT SWITCH (T-283) — the visible way into the live look.
//
// T-282 put Jason's real account on `?dataset=live`, which is a thing you have to be told. This is the thing
// you can find: the account name in the top bar becomes the trigger of a menu (DESIGN_SYSTEM §12 row 32 names
// "the account switcher" as one of the menu's three jobs) listing the sample account and every account the pull
// has written beside this app, with the one on screen marked and each pulled one carrying its own age.
//
// Three rules shape it:
//
//  1. **It lists what is really there.** The pulled accounts come from `live/index.json`, the file the pull
//     writes and `tools/preview-web.mjs` copies beside the build. No file, no live row — and then a quiet
//     "Connect your account" line instead, which opens the one command rather than pretending a setting exists.
//  2. **It does not fetch on a demo load.** A demo build is "no backend, no network" (REQ-001 AC1), and a
//     shipped snapshot has no `live/` folder, so asking for the index on every load would put a 404 in the
//     console of the thing we hand people. The index is read when the menu is first opened — or immediately on
//     a live build, where the folder demonstrably exists because the app booted from it.
//  3. **Choosing an account is a navigation** (`app/dataset.ts`): the address is what decides which dataset the
//     one ApiClient serves, and it is built once, before React. Rebuilding the client, its storage namespace
//     and the whole query cache in place would be a great deal of machinery for something a founder does once
//     a sitting — and the address staying true is what lets the choice be linked, bookmarked and reported.
//
// API builds use ConnectedAccountSwitch for server-listed accounts and guided onboarding (T-500).

/** The trigger's face: the avatar, the account name (which keeps `shell.account-name`), and the chevron. */
function AccountFace({ account, initials }: { account: AccountInfo; initials: string }) {
  return (
    <>
      <span className={styles.avatar} aria-hidden="true">
        {initials}
      </span>
      <span className={styles.accountName} data-testid={testId('shell', 'account-name')}>
        {account.name}
      </span>
    </>
  );
}

/** The account row when nothing can be switched (an `http` build): exactly what the shell drew before T-283. */
export function AccountLabel({ account, initials }: { account: AccountInfo; initials: string }) {
  return (
    <span className={styles.account}>
      <AccountFace account={account} initials={initials} />
    </span>
  );
}

export interface AccountSwitchProps {
  account: AccountInfo;
  initials: string;
}

export function AccountSwitch({ account, initials }: AccountSwitchProps) {
  const config = useAppConfig();
  const { messages, deviceTimeZone } = useShell();
  const clock = useClock();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const menuId = useId();
  const live = config.dataset === 'live';

  // Rule 2 above: on a demo build the index is read on the first open, not on load. `loadLiveIndex` answers
  // with an empty list rather than throwing, so a missing folder is a state and never an error.
  const index = useQuery({
    queryKey: ['live', 'index'],
    queryFn: () => loadLiveIndex(),
    enabled: open || live,
    staleTime: Infinity,
    retry: false,
  });
  const accounts: readonly LiveIndexEntry[] = index.data ?? [];
  // Which live row is the one on screen: the key the address named, or — `?dataset=live` with no key, and the
  // key the loader falls back on — the first entry.
  const currentKey = live ? (config.liveAccountKey ?? accounts[0]?.key ?? null) : null;
  const onLiveEntry = live && accounts.some((entry) => entry.key === currentKey);

  const items = useMemo<MenuEntry[]>(() => {
    const age = (entry: LiveIndexEntry): string =>
      entry.pulledAt
        ? SHELL_COPY.accountPulled(
            render(timeAgo(entry.pulledAt, clock.now(), deviceTimeZone), messages),
          )
        : SHELL_COPY.accountPulledUnknown;
    const note = (current: boolean, detail: string | null): string | undefined => {
      if (current)
        return detail === null ? SHELL_COPY.accountCurrent : SHELL_COPY.accountCurrentAnd(detail);
      return detail ?? undefined;
    };
    const entries: MenuEntry[] = [
      { type: 'label', key: 'heading', label: SHELL_COPY.accountSwitchTitle },
      {
        key: 'demo',
        // On the demo the label is the session's own name, so the menu and the top bar can never disagree; from
        // a live account the session cannot say what the demo is called, and `@sl/core`'s own name for the
        // sample account stands in (`DEMO_ACCOUNT_NAME`, 0.3.8 — one name on both builds, T-287).
        label: live ? DEMO_ACCOUNT_NAME : account.name + SHELL_COPY.demoAccountSuffix,
        icon: 'flask',
        end: note(!live, null),
        onSelect: () => goToDataset({ dataset: 'demo', liveAccountKey: null }),
      },
      ...accounts.map((entry) => ({
        key: `live-${entry.key}`,
        label: entry.name + SHELL_COPY.liveAccountSuffix,
        icon: 'lock' as const,
        end: note(entry.key === currentKey, age(entry)),
        onSelect: () => goToDataset({ dataset: 'live', liveAccountKey: entry.key }),
        testID: testId('shell', 'account-switch', 'live'),
      })),
    ];
    // The live account this build is SERVING but the index no longer lists — a re-pull under a new salt renames
    // every key. Better a row that says where you are than a menu that claims you are on the demo.
    if (live && !onLiveEntry) {
      entries.push({
        key: 'live-current',
        label: account.name + SHELL_COPY.liveAccountSuffix,
        icon: 'lock',
        end: SHELL_COPY.accountCurrent,
        onSelect: () => setOpen(false),
        testID: testId('shell', 'account-switch', 'live'),
      });
    }
    if (accounts.length === 0) {
      entries.push({ type: 'separator', key: 'sep' });
      entries.push(
        index.isPending
          ? {
              key: 'looking',
              label: SHELL_COPY.accountLooking,
              icon: 'loader',
              disabled: true,
              onSelect: () => {},
            }
          : {
              key: 'connect',
              label: LIVE_LOOK_COPY.connect,
              icon: 'plus',
              onSelect: () => setConnecting(true),
            },
      );
    }
    return entries;
  }, [
    accounts,
    account.name,
    clock,
    currentKey,
    deviceTimeZone,
    index.isPending,
    live,
    messages,
    onLiveEntry,
  ]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.account}
        data-testid={testId('shell', 'account-switch')}
        aria-label={`${account.name} — ${SHELL_COPY.accountSwitchTrigger}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((was) => !was)}
      >
        <AccountFace account={account} initials={initials} />
        <Icon name="chevron-down" size={14} className={styles.accountCaret} />
      </button>

      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        id={menuId}
        items={items}
        placement="bottom-start"
        width={320}
        /* An account row is a name with its provenance under it, not a command — see `.accountMenu`. */
        className={styles.accountMenu}
        aria-label={SHELL_COPY.accountSwitchTitle}
        testID={testId('shell', 'account-switch', 'menu')}
      />

      {/* D-165: a dialog is the whole screen at 360 px, so it carries the mode chip itself (REQ-006 AC5). */}
      <Modal
        open={connecting}
        onClose={() => setConnecting(false)}
        size="sm"
        title={LIVE_LOOK_COPY.connectTitle}
        subtitle={LIVE_LOOK_COPY.connectLead}
        returnFocusRef={triggerRef}
        testID={testId('shell', 'connect-account')}
        modeChip={<SurfaceModeChip testID={testId('shell', 'connect-account', 'mode-chip')} />}
        footer={
          <Button
            variant="ghost"
            testID={testId('shell', 'connect-account', 'close')}
            onClick={() => setConnecting(false)}
          >
            {LIVE_LOOK_COPY.connectClose}
          </Button>
        }
      >
        <p className={styles.connectNote}>{LIVE_LOOK_COPY.connectBody}</p>
        <PullCommand testID={testId('shell', 'connect-account', 'command')} />
      </Modal>
    </>
  );
}
